import React from 'react';
import {createBrowserRouter, Navigate, Outlet, RouteObject} from 'react-router-dom';
import {App} from '../App';
import {ProtectedRoute} from './components/ProtectedRoute';
import {Layout} from '../features/core/components/Layout';
import {Error} from '../features/status/pages/Error';
import {Success} from '../features/status/pages/Success';
import {Login} from '../features/auth/pages/Login';
import {LoginWithToken} from '../features/auth/pages/LoginWithToken';
import {Register} from '../features/auth/pages/Register';
import {ResetPassword} from '../features/auth/pages/ResetPassword';
import {Person} from '../features/person/pages/Person';
import {BiotwinList} from '../features/biotwin/pages/BiotwinList';
import {Profile} from '../features/profile/pages/Profile';
import {Biotwin} from '../features/biotwin/pages/Biotwin';
import {Biobook} from '../features/biobook/pages/Biobook';
import {Bookmarks} from '../features/biobook/pages/Bookmarks';
import {Updates} from '../features/biobook/pages/Updates';

// Less-used and heavier pages (admin, integrations, billing) load on demand to keep the main bundle small.
function lazyPage<M>(load: () => Promise<M>, name: keyof M) {
  const Page = React.lazy(() => load().then((module) => ({default: module[name] as React.ComponentType})));
  return function LazyPage() {
    return (
      <React.Suspense fallback={null}>
        <Page />
      </React.Suspense>
    );
  };
}
const Subscription = lazyPage(() => import('../features/profile/pages/Subscription'), 'Subscription');
const Welcome = lazyPage(() => import('../features/profile/pages/Welcome'), 'Welcome');
const SetPassword = lazyPage(() => import('../features/profile/pages/SetPassword'), 'SetPassword');
const ConfirmBiotwinShare = lazyPage(
  () => import('../features/biotwin-share/pages/ConfirmBiotwinShare'),
  'ConfirmBiotwinShare',
);
const Demo = lazyPage(() => import('../features/demo/pages/Demo'), 'Demo');
const IntegrationSimulator = lazyPage(
  () => import('../features/demo/pages/IntegrationSimulator'),
  'IntegrationSimulator',
);
const IntegrationDemo = lazyPage(() => import('../features/integration-demo/pages/IntegrationDemo'), 'IntegrationDemo');
const TenantDashboard = lazyPage(() => import('../features/tenant/pages/TenantDashboard'), 'TenantDashboard');
const TenantUserManagement = lazyPage(
  () => import('../features/tenant/pages/TenantUserManagement'),
  'TenantUserManagement',
);
const PatientBulkAdd = lazyPage(() => import('../features/tenant/pages/PatientBulkAdd'), 'PatientBulkAdd');
const AccountInfo = lazyPage(() => import('../features/profile/pages/AccountInfo'), 'AccountInfo');
const Membership = lazyPage(() => import('../features/profile/pages/Membership'), 'Membership');
const Notifications = lazyPage(() => import('../features/profile/pages/Notifications'), 'Notifications');

export const routes: RouteObject[] = [
  {
    path: '*',
    element: <App />,
    children: [
      {
        path: 'demo/:info?',
        element: <Demo />,
      },
      {
        path: 'error/:statusCode?',
        element: <Error />,
      },
      {
        path: 'success/:statusCode?',
        element: <Success />,
      },
      {
        path: 'sign-in',
        element: <Login />,
      },
      {
        path: 'sign-in/:token',
        element: <LoginWithToken />,
      },
      {
        path: 'sign-up',
        element: <Register />,
      },
      {
        path: 'reset-password',
        element: <ResetPassword />,
      },
      {
        path: '*',
        element: <ProtectedRoute children={<Layout className="layout" />} />,
        children: [
          {
            path: 'integration-demo',
            element: <IntegrationDemo />,
          },
          {
            path: 'welcome',
            element: <Welcome />,
            handle: {
              header: false,
              title: 'Welcome',
            },
          },
          {
            path: 'subscription',
            element: <Subscription />,
            handle: {
              header: false,
              title: 'Subscription',
            },
          },
          {
            path: 'set-password',
            element: <SetPassword />,
            handle: {
              header: false,
              title: 'Set Password',
            },
          },
          {
            path: 'confirm-biotwin-share',
            element: <ConfirmBiotwinShare />,
            handle: {
              header: false,
              title: 'Confirm Biotwin Share',
            },
          },
          {
            path: '*',
            element: <ProtectedRoute children={<Outlet />} protectionType="subscription" />,
            children: [
              {
                path: 'biotwins',
                element: <ProtectedRoute children={<BiotwinList />} protectionType="payment" />,
              },
              {
                path: 'biotwins/:userId',
                element: <ProtectedRoute children={<Person />} protectionType="payment" />,
                children: [
                  {
                    path: 'biotwin',
                    element: <Biotwin />,
                    handle: {
                      title: 'Biotwin',
                    },
                  },
                  {
                    path: 'biobook',
                    element: <Biobook />,
                    handle: {
                      title: 'Biobook',
                    },
                  },
                  {
                    path: 'bookmarks',
                    element: <Bookmarks />,
                    handle: {
                      title: 'Bookmarks',
                    },
                  },
                  {
                    path: 'updates',
                    element: <Updates />,
                    handle: {
                      title: 'Updates',
                    },
                  },
                  {
                    path: '*',
                    element: <Navigate to="biotwin" replace />,
                  },
                ],
              },
              {
                path: 'admin',
                element: <ProtectedRoute children={<Outlet />} protectionType="subscription" />,
                handle: {
                  pathname: '/admin/dashboard',
                  title: 'Admin Dashboard',
                },
                children: [
                  {
                    path: 'dashboard',
                    element: <TenantDashboard />,
                  },
                  {
                    path: 'user-management',
                    element: <TenantUserManagement />,
                    handle: {
                      title: 'User Management',
                    },
                  },
                  {
                    path: '*',
                    element: <Navigate to="dashboard" replace />,
                  },
                ],
              },
              {
                path: 'profile',
                element: <Profile />,
                handle: {
                  title: 'Profile',
                },
                children: [
                  {
                    path: 'account-info',
                    element: <ProtectedRoute children={<AccountInfo />} protectionType="payment" />,
                    handle: {
                      title: 'Account Info',
                    },
                  },
                  {
                    path: 'membership',
                    element: <Membership />,
                    handle: {
                      title: 'Subscription & Billing',
                    },
                  },
                  {
                    path: 'notifications',
                    element: <ProtectedRoute children={<Notifications />} protectionType="payment" />,
                    handle: {
                      title: 'Notifications',
                    },
                  },
                  {
                    path: '*',
                    element: <Navigate to="account-info" replace />,
                  },
                ],
              },
              {
                path: 'integration-simulator',
                element: (
                  <ProtectedRoute
                    children={<IntegrationSimulator />}
                    protectionType={({session}) => session?.isAdmin === 'true'}
                  />
                ),
              },
              {
                path: 'patient-bulk-add',
                element: (
                  <ProtectedRoute
                    children={<PatientBulkAdd />}
                    protectionType={({session}) => session?.isAdmin === 'true' || session?.isTenantAdmin === 'true'}
                  />
                ),
              },
              {
                path: '*',
                element: <Navigate to="/biotwins" replace />,
              },
            ],
          },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(
  routes.map((route) => ({
    ...route,
    errorElement: route.errorElement,
  })),
);
